import { useState, useEffect } from "react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Bell, Clock, Sprout, ClipboardCheck, ShieldAlert, UserCheck } from "lucide-react";
import { useProfile } from "@/hooks/useProfile";
import { toast } from "sonner";
import GlassCard from "./GlassCard";

const NotificationSettingsSheet = ({ open, onOpenChange }) => {
    const { profile, updateProfile, loading: profileLoading } = useProfile();
    const [saving, setSaving] = useState(false);

    const [preferences, setPreferences] = useState({
        pref_30_days: true,
        pref_7_days: true,
        pref_1_day: true,
        pref_overdue: true,
        pref_provider_notify: true,
        pref_plant_watering: true,
        pref_subtasks: true,
        pref_warranties: true,
        pref_time: "08:00",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });

    useEffect(() => {
        if (profile?.notification_preferences) {
            const timer = setTimeout(() => {
                setPreferences((prev) => ({
                    ...prev,
                    ...profile.notification_preferences,
                    timezone: profile.notification_preferences.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
                }));
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [profile]);

    const handleToggle = (key) => {
        setPreferences((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        const updatedPrefs = {
            ...preferences,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
        const success = await updateProfile({
            notification_preferences: updatedPrefs,
        });
        setSaving(false);
        if (success) {
            toast.success("Notification preferences updated");
            onOpenChange(false);
        }
    };

    const triggerHours = [
        { value: "05:00", label: "05:00 AM" },
        { value: "06:00", label: "06:00 AM" },
        { value: "07:00", label: "07:00 AM" },
        { value: "08:00", label: "08:00 AM" },
        { value: "09:00", label: "09:00 AM" },
        { value: "10:00", label: "10:00 AM" },
    ];

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="bottom"
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="max-h-[90vh] rounded-t-3xl flex flex-col pt-6"
                style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
            >
                <SheetHeader className="pb-4 flex-shrink-0 text-left">
                    <SheetTitle className="text-xl font-bold flex items-center gap-2">
                        <Bell className="w-5 h-5 text-primary" />
                        Notification Settings
                    </SheetTitle>
                    <SheetDescription>
                        Choose when and how alerts are sent to your phone.
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto space-y-4 scrollbar-hide py-1 px-1">
                    
                    {/* 1. COMPRESSED SERVICE REMINDER DAYS (CIRCLES CARD) */}
                    <GlassCard className="p-4 space-y-4">
                        <div>
                            <h4 className="font-bold text-foreground text-sm">Select Days Before Service</h4>
                            <p className="text-xs text-muted-foreground">Receive alerts at these milestones before service is due.</p>
                        </div>
                        <div className="flex justify-between items-center px-2">
                            {[
                                { key: "pref_30_days", label: "30 days", tag: "30d" },
                                { key: "pref_7_days", label: "7 days", tag: "7d" },
                                { key: "pref_1_day", label: "1 day", tag: "1d" },
                                { key: "pref_overdue", label: "1 day after", tag: "+1d" },
                            ].map((item) => {
                                const active = preferences[item.key];
                                return (
                                    <div
                                        key={item.key}
                                        onClick={() => handleToggle(item.key)}
                                        className="flex flex-col items-center gap-1.5 cursor-pointer group"
                                    >
                                        <div className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all ${
                                            active
                                                ? "bg-primary border-primary text-primary-foreground shadow-sm shadow-primary/20 scale-105"
                                                : "bg-transparent border-muted-foreground/30 text-muted-foreground group-hover:border-muted-foreground/50"
                                        }`}>
                                            <span className="text-xs font-bold">{item.tag}</span>
                                        </div>
                                        <span className="text-[10px] text-muted-foreground font-medium">{item.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </GlassCard>

                    {/* 2. TRIGGER TIME PREFERENCE */}
                    <GlassCard className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Clock className="w-5 h-5 text-primary shrink-0" />
                            <div>
                                <h4 className="font-bold text-foreground text-sm">Morning Alert Time</h4>
                                <p className="text-xs text-muted-foreground">Select when push alerts land on your device.</p>
                            </div>
                        </div>
                        <select
                            value={preferences.pref_time || "08:00"}
                            onChange={(e) => setPreferences(prev => ({ ...prev, pref_time: e.target.value }))}
                            className="bg-secondary/50 px-3 py-1.5 rounded-lg border border-border text-sm font-semibold focus:outline-none cursor-pointer"
                        >
                            {triggerHours.map((h) => (
                                <option key={h.value} value={h.value} className="bg-popover text-foreground">
                                    {h.label}
                                </option>
                            ))}
                        </select>
                    </GlassCard>

                    {/* 3. ADDITIONAL REMINDERS */}
                    <div className="space-y-3">
                        {/* Plants */}
                        <GlassCard className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => handleToggle('pref_plant_watering')}>
                            <Checkbox
                                checked={preferences.pref_plant_watering}
                                onCheckedChange={() => handleToggle('pref_plant_watering')}
                                className="w-6 h-6 rounded-lg border-2 border-primary/20 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                            />
                            <div className="flex-1 flex items-center gap-3">
                                <Sprout className="w-5 h-5 text-primary shrink-0" />
                                <div>
                                    <h4 className="font-bold text-foreground text-sm">Plant Watering</h4>
                                    <p className="text-xs text-muted-foreground">Alerts for plants due for watering today.</p>
                                </div>
                            </div>
                        </GlassCard>

                        {/* Subtasks */}
                        <GlassCard className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => handleToggle('pref_subtasks')}>
                            <Checkbox
                                checked={preferences.pref_subtasks}
                                onCheckedChange={() => handleToggle('pref_subtasks')}
                                className="w-6 h-6 rounded-lg border-2 border-primary/20 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                            />
                            <div className="flex-1 flex items-center gap-3">
                                <ClipboardCheck className="w-5 h-5 text-primary shrink-0" />
                                <div>
                                    <h4 className="font-bold text-foreground text-sm">Maintenance Sub-Tasks</h4>
                                    <p className="text-xs text-muted-foreground">Alerts for appliance & furniture sub-tasks due today.</p>
                                </div>
                            </div>
                        </GlassCard>

                        {/* Warranties */}
                        <GlassCard className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => handleToggle('pref_warranties')}>
                            <Checkbox
                                checked={preferences.pref_warranties}
                                onCheckedChange={() => handleToggle('pref_warranties')}
                                className="w-6 h-6 rounded-lg border-2 border-primary/20 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                            />
                            <div className="flex-1 flex items-center gap-3">
                                <ShieldAlert className="w-5 h-5 text-primary shrink-0" />
                                <div>
                                    <h4 className="font-bold text-foreground text-sm">Warranty Expiry</h4>
                                    <p className="text-xs text-muted-foreground">Alerts 30 days before product warranties expire.</p>
                                </div>
                            </div>
                        </GlassCard>
                    </div>

                    {/* 4. SERVICE PROVIDERS AUTOMATION */}
                    <GlassCard className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => handleToggle('pref_provider_notify')}>
                        <Checkbox
                            checked={preferences.pref_provider_notify}
                            onCheckedChange={() => handleToggle('pref_provider_notify')}
                            className="w-6 h-6 rounded-lg border-2 border-primary/20 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                        />
                        <div className="flex-1 flex items-center gap-3">
                            <UserCheck className="w-5 h-5 text-primary shrink-0" />
                            <div>
                                <h4 className="font-bold text-foreground text-sm">Auto-notify Service Provider</h4>
                                <p className="text-xs text-muted-foreground">
                                    Automatically email the service provider 30 days before service is due.
                                </p>
                            </div>
                        </div>
                    </GlassCard>

                </div>

                <div className="flex-shrink-0 pt-4">
                    <Button
                        onClick={handleSave}
                        className="w-full rounded-xl py-6 text-base font-semibold shadow-lg shadow-primary/20"
                        disabled={saving || profileLoading}
                    >
                        {saving ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                Saving Preferences...
                            </>
                        ) : (
                            "Save Changes"
                        )}
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default NotificationSettingsSheet;
